import { useState, useRef, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useChatContext } from '@/contexts/ChatContext';
import { useAuth } from '@/contexts/AuthContext';
import { usePlans } from '@/contexts/PlansContext';
import { useToast } from '@/contexts/ToastContext';
import { motion, AnimatePresence } from 'framer-motion';
import { PlanType } from '@/api/plans';
import StripeCheckoutModal from '@/components/dashboard/StripeCheckoutModal';
import { chatsApi } from '@/api/chats';
import { GeminiIcon, ClaudeIcon, ChatGptIcon, DeepSeekIcon } from './LlmIcons';
import type {
  ChatInputContextResponse,
  ChatInputContextClickUpTask,
  ChatInputContextClickUpList,
  ChatInputContextClickUpWorkspace,
  ChatInputContextClickUpSpace,
} from '@/api/chats';


import { Check, Network } from 'lucide-react';

type AttachmentItem = {
  id: string;
  file: File;
};

type LlmSelection =
  | { kind: 'auto' }
  | { kind: 'model'; provider: string; model: string };

interface ChatInputProps {
  iconOnly?: boolean;
}

export default function ChatInput({ iconOnly = false }: ChatInputProps) {
  const [input, setInput] = useState('');
  const [attachments, setAttachments] = useState<AttachmentItem[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const editableRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const llmMenuRef = useRef<HTMLDivElement>(null);
  const [isLlmMenuOpen, setIsLlmMenuOpen] = useState(false);
  const [selectedLlm, setSelectedLlm] = useState<LlmSelection>({
    kind: 'model',
    provider: 'gpt',
    model: 'gpt-4o',
  });
  const [lastManualLlm, setLastManualLlm] = useState<{
    provider: string;
    model: string;
  }>({ provider: 'gpt', model: 'gpt-4o' });
  const [llmSearch, setLlmSearch] = useState('');
  const [llmHighlightIndex, setLlmHighlightIndex] = useState(-1);
  const llmListRef = useRef<HTMLDivElement>(null);
  const [showCommandMenu, setShowCommandMenu] = useState(false);
  const [selectedCommandIndex, setSelectedCommandIndex] = useState(0);
  const [commandQuery, setCommandQuery] = useState('');
  const commandMenuRef = useRef<HTMLDivElement>(null);
  const [clickupItems, setClickupItems] = useState<Array<{ id: string; name: string; breadcrumb: string; type: 'task' | 'list' }>>([]);
  const [clickupLoading, setClickupLoading] = useState(false);
  const [showMentionMenu, setShowMentionMenu] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const mentionMenuRef = useRef<HTMLDivElement>(null);
  const [_mentionedChips, setMentionedChips] = useState<Array<{ id: string; name: string; label: string }>>([]);
  const [isEditableEmpty, setIsEditableEmpty] = useState(true);
  const [isStripeCheckoutOpen, setIsStripeCheckoutOpen] = useState(false);
  const navigate = useNavigate();
  const { user, refreshCredits: refreshCreditsFromAuth } = useAuth();
  const { planType: userPlanType, proPlanId, refreshPlans } = usePlans();
  const credits = user?.credits ?? 0;
  const location = useLocation();
  const pathname = location.pathname;

  // Get current chatId from URL if available
  const currentChatIdFromUrl = useMemo(() => {
    // Match both formats: /dashboard/chat/:chatId and /dashboard/chat/:projectId/:chatId
    const match = pathname.match(/\/dashboard\/chat\/(?:[^/]+\/)?([^/]+)$/);
    return match ? match[1] : null;
  }, [pathname]);
  const toast = useToast();

  const {
    isStreaming,
    isTypingAnimation,
    registerChatInput,
    sendMessage,
    selectedProjectId,
    clearChat,
    workspaceId,
    suggestedMessage,
    setSuggestedMessage,
    focusChatInput,
    chatMode,
  } = useChatContext();

  const isBusy = isStreaming || isTypingAnimation;

  const llmModels = [
    {
      provider: 'gpt',
      name: 'OpenAI (GPT)',
      models: [
        { id: 'gpt-5', name: 'GPT-5', description: 'Modelo flagship para tarefas complexas' },
        { id: 'gpt-5-mini', name: 'GPT-5 Mini', description: 'Mais rápido e econômico para a maioria dos casos' },
        { id: 'gpt-4.1', name: 'GPT-4.1', description: 'Ótimo equilíbrio de qualidade e custo' },
        { id: 'gpt-4o', name: 'GPT-4o', description: 'Multimodal, rápido e consistente' },
        { id: 'gpt-4o-mini', name: 'GPT-4o Mini', description: 'Baixo custo e alta velocidade' },
      ],
    },
    {
      provider: 'gemini',
      name: 'Google Gemini',
      models: [
        { id: 'gemini-3.1-pro-preview', name: 'Gemini 3.1 Pro (Preview)', description: 'Top para raciocínio e qualidade' },
        { id: 'gemini-3-pro-preview', name: 'Gemini 3 Pro (Preview)', description: 'Qualidade alta e consistente' },
        { id: 'gemini-3-flash-preview', name: 'Gemini 3 Flash (Preview)', description: 'Rápido e econômico' },
      ],
    },
    {
      provider: 'claude',
      name: 'Anthropic Claude',
      models: [
        { id: 'claude-4-opus', name: 'Claude 4 Opus', description: 'Máxima capacidade para tarefas difíceis' },
        { id: 'claude-4-sonnet', name: 'Claude 4 Sonnet', description: 'Excelente equilíbrio e qualidade' },
        { id: 'claude-4-haiku', name: 'Claude 4 Haiku', description: 'Rápido e econômico' },
        { id: 'claude-3.7-sonnet', name: 'Claude 3.7 Sonnet', description: 'Qualidade alta para raciocínio' },
        { id: 'claude-3.5-sonnet', name: 'Claude 3.5 Sonnet', description: 'Qualidade alta e consistente' },
        { id: 'claude-3.5-haiku', name: 'Claude 3.5 Haiku', description: 'Baixo custo e alta velocidade' },
      ],
    },
    {
      provider: 'xai',
      name: 'xAI (Grok)',
      models: [
        { id: 'grok-4', name: 'Grok 4', description: 'Raciocínio e tarefas gerais' },
        { id: 'grok-4.1', name: 'Grok 4.1', description: 'Melhorias incrementais e qualidade' },
        { id: 'grok-4.1-fast', name: 'Grok 4.1 Fast', description: 'Mais rápido e econômico' },
      ],
    },
    {
      provider: 'deepseek',
      name: 'DeepSeek',
      models: [
        { id: 'deepseek-v3', name: 'DeepSeek V3', description: 'Ótimo para conversas gerais e raciocínio' },
        { id: 'deepseek-coder', name: 'DeepSeek Coder', description: 'Especializado em geração e análise de código' },
      ],
    }
  ];

  const getLlmIcon = (provider: string) => {
    switch (provider) {
      case 'auto':
        return <Network className="w-4 h-4 text-current" />;
      case 'gpt':
        return <ChatGptIcon className="w-4 h-4" />;
      case 'gemini':
        return <GeminiIcon className="w-4 h-4" />;
      case 'claude':
        return <ClaudeIcon className="w-4 h-4" />;
      case 'deepseek':
        return <DeepSeekIcon className="w-4 h-4" />;
      case 'xai':
        return <span className="text-[11px] font-semibold leading-none">xAI</span>;
      case 'paladas':
        return <img src="/logo-mini.svg" alt="Paladas" className="w-4 h-4" />;
      default:
        return <span className="text-[13px]">⚡</span>;
    }
  };

  const toggleAutoSelection = () => {
    setLlmSearch('');
    setSelectedLlm((prev) => {
      if (prev.kind === 'auto') {
        return { kind: 'model', provider: lastManualLlm.provider, model: lastManualLlm.model };
      }
      return { kind: 'auto' };
    });
  };

  const getSelectedLlmDisplay = () => {
    if (selectedLlm.kind === 'auto') return 'Auto';
    const provider = llmModels.find((p) => p.provider === selectedLlm.provider);
    if (!provider) return 'Selecionar modelo';
    const model = provider.models.find((m) => m.id === selectedLlm.model);
    return model ? model.name : 'Selecionar modelo';
  };

  const showModels = selectedLlm.kind !== 'auto';

  type LlmModelsGroup = { provider: (typeof llmModels)[number]; models: (typeof llmModels)[number]['models'] };

  const llmModelsFilteredByProvider: LlmModelsGroup[] = useMemo(() => {
    const q = llmSearch.toLowerCase().trim();
    if (!q) return llmModels.map((p) => ({ provider: p, models: p.models }));

    return llmModels
      .map((p) => {
        const providerMatch = p.name.toLowerCase().includes(q) || p.provider.toLowerCase().includes(q);
        const models = providerMatch
          ? p.models
          : p.models.filter(
            (m) =>
              m.name.toLowerCase().includes(q) ||
              m.id.toLowerCase().includes(q)
          );
        return { provider: p, models };
      })
      .filter((g) => g.models.length > 0);
  }, [llmSearch]);

  type LlmSelectableMenuOption =
    | { kind: 'auto' }
    | { kind: 'model'; providerId: string; providerName: string; model: { id: string; name: string; description: string } };

  const llmSelectableOptions: LlmSelectableMenuOption[] = useMemo(() => {
    if (!showModels) return [{ kind: 'auto' }];
    const options: LlmSelectableMenuOption[] = [{ kind: 'auto' }];
    for (const group of llmModelsFilteredByProvider) {
      for (const model of group.models) {
        options.push({
          kind: 'model',
          providerId: group.provider.provider,
          providerName: group.provider.name,
          model,
        });
      }
    }
    return options;
  }, [llmModelsFilteredByProvider, showModels]);

  type LlmModelsMenuEntry =
    | { kind: 'provider-header'; providerId: string; providerName: string }
    | { kind: 'model'; selectableIndex: number; providerId: string; model: { id: string; name: string; description: string } };

  const llmModelsMenuEntries: LlmModelsMenuEntry[] = useMemo(() => {
    if (!showModels) return [];
    let selectableIndex = 1;
    const entries: LlmModelsMenuEntry[] = [];
    for (const group of llmModelsFilteredByProvider) {
      entries.push({
        kind: 'provider-header',
        providerId: group.provider.provider,
        providerName: group.provider.name,
      });
      for (const model of group.models) {
        entries.push({
          kind: 'model',
          selectableIndex,
          providerId: group.provider.provider,
          model,
        });
        selectableIndex += 1;
      }
    }
    return entries;
  }, [llmModelsFilteredByProvider, showModels]);

  const llmSelectableCount = llmSelectableOptions.length;

  const MENTIONS = [
    { id: 'clickup', name: 'ClickUp', label: 'ClickUp', icon: '/icons/integrations/clickup.svg' },
    { id: 'github', name: 'GitHub', label: 'GitHub', icon: '/icons/integrations/github.svg' },
    { id: 'slack', name: 'Slack', label: 'Slack', icon: '/icons/integrations/slack.svg' },
    { id: 'jira', name: 'Jira', label: 'Jira', icon: '/icons/integrations/jira.svg' },
  ];

  function flattenClickUpSummary(res: ChatInputContextResponse): Array<{ id: string; name: string; breadcrumb: string; type: 'task' | 'list' }> {
    const items: Array<{ id: string; name: string; breadcrumb: string; type: 'task' | 'list' }> = [];
    if (!res.clickup?.workspaces?.length) return items;
    for (const ws of res.clickup.workspaces as ChatInputContextClickUpWorkspace[]) {
      for (const space of ws.spaces || []) {
        const spacePath = `${ws.name} › ${(space as ChatInputContextClickUpSpace).name}`;
        for (const folder of (space as ChatInputContextClickUpSpace).folders || []) {
          for (const list of folder.lists || []) {
            const listPath = `${spacePath} › ${folder.name} › ${(list as ChatInputContextClickUpList).name}`;
            for (const task of (list as ChatInputContextClickUpList).tasks || []) {
              items.push({
                id: (task as ChatInputContextClickUpTask).id,
                name: (task as ChatInputContextClickUpTask).name,
                breadcrumb: listPath,
                type: 'task',
              });
            }
            if (!(list as ChatInputContextClickUpList).tasks?.length) {
              items.push({
                id: (list as ChatInputContextClickUpList).id,
                name: (list as ChatInputContextClickUpList).name,
                breadcrumb: `${spacePath} › ${folder.name}`,
                type: 'list',
              });
            }
          }
        }
        for (const list of (space as ChatInputContextClickUpSpace).folderlessLists || []) {
          const listPath = `${spacePath} › ${(list as ChatInputContextClickUpList).name}`;
          for (const task of (list as ChatInputContextClickUpList).tasks || []) {
            items.push({
              id: (task as ChatInputContextClickUpTask).id,
              name: (task as ChatInputContextClickUpTask).name,
              breadcrumb: listPath,
              type: 'task',
            });
          }
          if (!(list as ChatInputContextClickUpList).tasks?.length) {
            items.push({
              id: (list as ChatInputContextClickUpList).id,
              name: (list as ChatInputContextClickUpList).name,
              breadcrumb: spacePath,
              type: 'list',
            });
          }
        }
      }
    }
    return items;
  }

  /** Abstrai "/clickup" e "clickup:" — só o que importa é o termo de filtro (ex.: "/clickup:Task" → "Task"). */
  const getClickUpFilterQuery = (raw: string) =>
    raw.replace(/^\//, '').replace(/^clickup:?/i, '').trim();

  const filteredClickupItems = useMemo(() => {
    const searchQuery = getClickUpFilterQuery(commandQuery);
    if (!searchQuery) return clickupItems;
    const q = searchQuery.toLowerCase();
    return clickupItems.filter(
      (item) =>
        item.name.toLowerCase().includes(q) ||
        item.breadcrumb.toLowerCase().includes(q)
    );
  }, [clickupItems, commandQuery]);

  type CommandMenuItem = { kind: 'clickup'; id: string; name: string; breadcrumb: string; type: 'task' | 'list' };

  const commandMenuList: CommandMenuItem[] = useMemo(() => {
    return filteredClickupItems.map((item) => ({
      kind: 'clickup' as const,
      id: item.id,
      name: item.name,
      breadcrumb: item.breadcrumb,
      type: item.type,
    }));
  }, [filteredClickupItems]);

  useEffect(() => {
    if (!showCommandMenu || !workspaceId) {
      setClickupItems([]);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      setClickupLoading(true);
      const searchQuery = getClickUpFilterQuery(commandQuery);
      chatsApi
        .getInputSummary(searchQuery || undefined)
        .then((res) => {
          if (!cancelled) {
            setClickupItems(flattenClickUpSummary(res));
          }
        })
        .catch(() => {
          if (!cancelled) setClickupItems([]);
        })
        .finally(() => {
          if (!cancelled) setClickupLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [showCommandMenu, workspaceId, commandQuery]);

  const filteredMentions = useMemo(() => {
    if (!mentionQuery.trim()) return MENTIONS;
    const q = mentionQuery.toLowerCase().trim();
    return MENTIONS.filter(
      (m) =>
        m.name.toLowerCase().includes(q) ||
        m.id.toLowerCase().includes(q)
    );
  }, [mentionQuery]);

  useEffect(() => {
    if (!showCommandMenu) return;

    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (commandMenuRef.current && !commandMenuRef.current.contains(target)) {
        setShowCommandMenu(false);
        setCommandQuery('');
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowCommandMenu(false);
        setCommandQuery('');
      }
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [showCommandMenu]);

  useEffect(() => {
    if (!showMentionMenu) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (mentionMenuRef.current && !mentionMenuRef.current.contains(target) && !(target && editableRef.current?.contains(target))) {
        setShowMentionMenu(false);
        setMentionQuery('');
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowMentionMenu(false);
        setMentionQuery('');
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [showMentionMenu]);

  useEffect(() => {
    if (!isLlmMenuOpen) return;

    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (llmMenuRef.current && !llmMenuRef.current.contains(target)) {
        setIsLlmMenuOpen(false);
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsLlmMenuOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isLlmMenuOpen]);

  useEffect(() => {
    if (isLlmMenuOpen) {
      setLlmSearch('');
      setLlmHighlightIndex(0);
    }
  }, [isLlmMenuOpen]);

  useEffect(() => {
    if (!isLlmMenuOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target?.closest?.('input') || target?.closest?.('textarea')) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setLlmHighlightIndex((i) =>
          i < llmSelectableCount - 1 ? i + 1 : 0
        );
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setLlmHighlightIndex((i) =>
          i > 0 ? i - 1 : llmSelectableCount - 1
        );
        return;
      }
      if (e.key === 'Enter' && llmHighlightIndex >= 0 && llmSelectableOptions[llmHighlightIndex]) {
        e.preventDefault();
        const o = llmSelectableOptions[llmHighlightIndex];
        if (o.kind === 'auto') {
          toggleAutoSelection();
          return;
        }
        if (o.kind === 'model') {
          setLastManualLlm({ provider: o.providerId, model: o.model.id });
          setSelectedLlm({ kind: 'model', provider: o.providerId, model: o.model.id });
          setIsLlmMenuOpen(false);
        }
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setIsLlmMenuOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isLlmMenuOpen, llmHighlightIndex, llmSelectableCount, llmSelectableOptions]);

  useEffect(() => {
    if (!isLlmMenuOpen) return;
    setLlmHighlightIndex((prev) => Math.max(0, Math.min(prev, llmSelectableCount - 1)));
  }, [isLlmMenuOpen, llmSelectableCount]);

  useEffect(() => {
    if (!isLlmMenuOpen) return;
    if (!showModels) {
      setLlmHighlightIndex(0);
      return;
    }
    if (!llmSearch.trim()) {
      setLlmHighlightIndex(0);
      return;
    }
    setLlmHighlightIndex(llmModelsFilteredByProvider.length > 0 ? 1 : 0);
  }, [isLlmMenuOpen, llmSearch, llmModelsFilteredByProvider.length, showModels]);

  useEffect(() => {
    if (!isLlmMenuOpen || llmHighlightIndex < 0) return;
    const el = llmListRef.current?.querySelector(`[data-llm-index="${llmHighlightIndex}"]`);
    el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [isLlmMenuOpen, llmHighlightIndex]);

  useEffect(() => {
    registerChatInput(editableRef.current);
    return () => registerChatInput(null);
  }, [registerChatInput]);

  // When a quick action button sets a suggested message, fill the input and focus
  useEffect(() => {
    if (!suggestedMessage || suggestedMessage.trim() === '') return;
    setInput(suggestedMessage);
    setIsEditableEmpty(false);
    if (editableRef.current) {
      editableRef.current.textContent = suggestedMessage;
    }
    setSuggestedMessage(null);
    focusChatInput();
  }, [suggestedMessage, setSuggestedMessage, focusChatInput]);

  useEffect(() => {
    if (!showCommandMenu) return;
    const len = commandMenuList.length;
    setSelectedCommandIndex((prev) => (len > 0 ? Math.min(prev, len - 1) : 0));
  }, [showCommandMenu, commandMenuList.length]);

  const handleSubmit = async () => {
    const { message: rawMessage, mentions: rawMentions } = editableRef.current ? serializeEditable(editableRef.current) : { message: '', mentions: [] };
    const message = rawMessage.trim();
    if (!message || isBusy) return;

    const uiAttachments = attachments.map((a) => ({
      name: a.file.name,
      size: a.file.size,
      type: a.file.type,
      previewUrl: a.file.type?.startsWith('image/') ? URL.createObjectURL(a.file) : undefined,
    }));

    setInput('');
    setAttachmentError(null);
    setMentionedChips([]);
    setIsEditableEmpty(true);
    if (editableRef.current) {
      editableRef.current.innerHTML = '';
    }

    try {
      const metadataBase =
        attachments.length
          ? {
            attachments: attachments.map((a) => ({
              name: a.file.name,
              size: a.file.size,
              type: a.file.type,
            })),
          }
          : undefined;

      const metadataMaybe = {
        ...(metadataBase || {}),
        ...(selectedLlm.kind === 'auto'
          ? { model: 'openrouter/free' }
          : { model: selectedLlm.model }),
        ...(rawMentions.length > 0 ? { mentions: rawMentions.map((c) => c.id) } : {}),
        ...(chatMode !== 'default' ? { chatMode } : {}),
      };
      const metadata = Object.keys(metadataMaybe).length > 0 ? metadataMaybe : undefined;

      const chatId = await sendMessage(message, {
        uiAttachments,
        files: attachments.map((a) => a.file),
        metadata,
      });

      if (chatId) {
        // Use new route format with projectId if available, otherwise use old format
        const expectedPath = selectedProjectId
          ? `/dashboard/chat/${selectedProjectId}/${chatId}`
          : `/dashboard/chat/${chatId}`;

        // Navigate if:
        // 1. We're not on the chat route at all (e.g., on /dashboard)
        // 2. We're on a different chat route
        const shouldNavigate = currentChatIdFromUrl !== chatId;

        if (shouldNavigate) {
          navigate(expectedPath, { replace: true });
        }
      }
      setAttachments([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      // Credits are synced from server by ChatContext (refreshCredits) after the message is sent; backend consumes credits
    } catch (error) {
      console.error('Error sending message:', error);
      // Error handling is done in sendMessage function
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (showMentionMenu && filteredMentions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedMentionIndex((prev) => (prev + 1) % filteredMentions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedMentionIndex((prev) => (prev - 1 + filteredMentions.length) % filteredMentions.length);
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        const selected = filteredMentions[selectedMentionIndex];
        if (selected) insertMention(selected);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowMentionMenu(false);
        setMentionQuery('');
        return;
      }
    }

    if (showCommandMenu) {
      const listLen = commandMenuList.length;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedCommandIndex((prev) => (prev + 1) % (listLen || 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedCommandIndex((prev) => (prev - 1 + (listLen || 1)) % (listLen || 1));
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        const selected = commandMenuList[selectedCommandIndex];
        if (selected) {
          insertCommand(`[ClickUp: ${selected.name}](clickup:${selected.id})`);
        }
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowCommandMenu(false);
        setCommandQuery('');
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey && !isBusy && !showCommandMenu && !showMentionMenu) {
      e.preventDefault();
      handleSubmit();
    }

    if (e.key === 'Backspace' && !showCommandMenu && !showMentionMenu) {
      const container = editableRef.current;
      const sel = window.getSelection();
      if (container && sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        if (range.collapsed && range.startOffset > 0) {
          const node = range.startContainer;
          const prev = node.nodeType === Node.TEXT_NODE
            ? (range.startOffset === 0 ? node.previousSibling : null)
            : node.childNodes[range.startOffset - 1];
          if (prev && (prev as HTMLElement).getAttribute?.('data-mention-id')) {
            e.preventDefault();
            prev.remove();
            container.normalize();
            handleEditableInput();
          }
        }
      }
    }
  };

  const insertCommand = (command: string) => {
    const container = editableRef.current;
    if (!container) return;

    if (command === '/clear') {
      clearChat();
      setInput('');
      setMentionedChips([]);
      setShowCommandMenu(false);
      setCommandQuery('');
      container.innerHTML = '';
      container.focus();
      return;
    }

    const textBefore = getTextBeforeCaret(container);
    const lastSlashIndex = textBefore.lastIndexOf('/');
    if (lastSlashIndex === -1) return;

    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const startPos = getNodeAtOffset(container, lastSlashIndex);
    const endRange = sel.getRangeAt(0);
    if (!startPos) return;

    const range = document.createRange();
    range.setStart(startPos.node, startPos.offset);
    range.setEnd(endRange.endContainer, endRange.endOffset);
    range.deleteContents();
    const textNode = document.createTextNode(command + ' ');
    range.insertNode(textNode);
    range.setStart(textNode, textNode.length);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
    handleEditableInput();
    setShowCommandMenu(false);
    setCommandQuery('');
  };

  function getNodeAtOffset(container: Node, targetOffset: number): { node: Node; offset: number } | null {
    let current = 0;
    const walk = (node: Node): { node: Node; offset: number } | null => {
      if (node.nodeType === Node.TEXT_NODE) {
        const len = (node.textContent || '').length;
        if (current + len >= targetOffset) return { node, offset: targetOffset - current };
        current += len;
        return null;
      }
      if (node.nodeType === Node.ELEMENT_NODE && (node as HTMLElement).getAttribute?.('data-mention-id')) {
        const len = (node.textContent || '').length || 1;
        if (current + len > targetOffset) return { node: node.parentNode!, offset: Array.from(node.parentNode!.childNodes).indexOf(node as ChildNode) };
        current += len;
        return null;
      }
      for (let i = 0; i < node.childNodes.length; i++) {
        const res = walk(node.childNodes[i]);
        if (res) return res;
      }
      return null;
    };
    return walk(container);
  }

  const insertMention = (mention: { id: string; name: string; label: string }) => {
    const container = editableRef.current;
    if (!container) return;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const textBefore = getTextBeforeCaret(container);
    const lastAtIndex = textBefore.lastIndexOf('@');
    if (lastAtIndex === -1) return;

    const startPos = getNodeAtOffset(container, lastAtIndex);
    const endRange = sel.getRangeAt(0);
    if (!startPos) return;

    const range = document.createRange();
    range.setStart(startPos.node, startPos.offset);
    range.setEnd(endRange.endContainer, endRange.endOffset);
    range.deleteContents();

    const chip = document.createElement('span');
    chip.contentEditable = 'false';
    chip.setAttribute('data-mention-id', mention.id);
    chip.className = 'mention-chip inline-flex items-center gap-1 pl-1.5 pr-1 py-0.5 rounded-md text-xs font-medium theme-text border theme-border bg-[color:color-mix(in_oklab,var(--card-bg)_90%,transparent)] align-middle mx-0.5 shrink-0';
    const icon = document.createElement('img');
    icon.src = MENTIONS.find((m) => m.id === mention.id)?.icon ?? '/icons/feature/code.svg';
    icon.alt = '';
    icon.className = 'w-4 h-4 rounded';
    chip.appendChild(icon);
    chip.appendChild(document.createTextNode(mention.label));
    const closeBtn = document.createElement('span');
    closeBtn.setAttribute('role', 'button');
    closeBtn.setAttribute('aria-label', `Remover ${mention.label}`);
    closeBtn.setAttribute('tabindex', '0');
    closeBtn.className = 'mention-chip-close inline-flex items-center justify-center ml-1 w-4 min-w-[16px] h-4 rounded cursor-pointer theme-text-muted hover:theme-text hover:bg-black/10 dark:hover:bg-white/10 select-none flex-shrink-0';
    closeBtn.style.fontSize = '14px';
    closeBtn.style.lineHeight = '1';
    closeBtn.textContent = '×';
    closeBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      chip.remove();
      editableRef.current?.normalize();
      handleEditableInput();
    });
    chip.appendChild(closeBtn);

    range.insertNode(chip);
    const space = document.createTextNode('\u00A0');
    chip.after(space);
    range.setStart(space, 1);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
    container.normalize();
    handleEditableInput();
    setShowMentionMenu(false);
    setMentionQuery('');
  };

  function serializeEditable(container: HTMLDivElement | null): { message: string; mentions: Array<{ id: string; name: string; label: string }> } {
    if (!container) return { message: '', mentions: [] };
    let message = '';
    const mentions: Array<{ id: string; name: string; label: string }> = [];
    const walk = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        message += node.textContent || '';
        return;
      }
      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        if (el.getAttribute?.('data-mention-id')) {
          const id = el.getAttribute('data-mention-id') || '';
          const m = MENTIONS.find((x) => x.id === id);
          message += `@${m?.name ?? id} `;
          if (m && !mentions.some((x) => x.id === id)) mentions.push({ id: m.id, name: m.name, label: m.label });
          return;
        }
      }
      node.childNodes.forEach(walk);
    };
    container.childNodes.forEach(walk);
    return { message: message.trim(), mentions };
  }

  function getTextBeforeCaret(container: HTMLDivElement | null): string {
    if (!container) return '';
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return '';
    const range = sel.getRangeAt(0).cloneRange();
    range.setStart(container, 0);
    return range.toString();
  }

  const handleEditableInput = () => {
    const el = editableRef.current;
    if (!el) return;
    const { message, mentions } = serializeEditable(el);
    const empty = !(el.textContent || '').trim() && el.querySelectorAll('[data-mention-id]').length === 0;
    setIsEditableEmpty(empty);
    setInput(message);
    setMentionedChips(mentions);
    const textBefore = getTextBeforeCaret(el);
    const lastAt = textBefore.lastIndexOf('@');
    if (lastAt !== -1) {
      const afterAt = textBefore.slice(lastAt + 1);
      if (!afterAt.includes(' ') && !afterAt.includes('\n')) {
        setShowMentionMenu(true);
        setMentionQuery(afterAt);
        setSelectedMentionIndex(0);
        setShowCommandMenu(false);
        return;
      }
    }
    setShowMentionMenu(false);
    setMentionQuery('');
    const lastSlash = textBefore.lastIndexOf('/');
    if (lastSlash !== -1) {
      const afterSlash = textBefore.slice(lastSlash + 1);
      if (!afterSlash.includes(' ') && !afterSlash.includes('\n')) {
        setShowCommandMenu(true);
        setCommandQuery(afterSlash);
        setSelectedCommandIndex(0);
        return;
      }
    }
    setShowCommandMenu(false);
    setCommandQuery('');
  };

  const isDisabled = false;

  const handlePickFiles = () => {
    if (isDisabled || isBusy) return;
    fileInputRef.current?.click();
  };

  const handleFilesSelected = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setAttachmentError(null);

    const MAX_FILES = 5;
    const MAX_TOTAL_BYTES = 25 * 1024 * 1024; // 25MB

    const incoming = Array.from(files);

    setAttachments((prev) => {
      const existingKey = new Set(prev.map((a) => `${a.file.name}:${a.file.size}:${a.file.lastModified}`));
      const next = [...prev];

      for (const f of incoming) {
        const key = `${f.name}:${f.size}:${f.lastModified}`;
        if (existingKey.has(key)) continue;
        next.push({ id: `att-${Date.now()}-${Math.random()}`, file: f });
        existingKey.add(key);
      }

      if (next.length > MAX_FILES) {
        setAttachmentError(`Você pode anexar no máximo ${MAX_FILES} arquivos.`);
        return next.slice(0, MAX_FILES);
      }

      const total = next.reduce((acc, a) => acc + (a.file.size || 0), 0);
      if (total > MAX_TOTAL_BYTES) {
        setAttachmentError('Os anexos excedem o limite de 25MB no total.');
      }

      return next;
    });
  };

  const removeAttachment = (id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  return (

    <div className="relative w-full mx-auto">
      {/* Credits Banner */}
      {!iconOnly && (
        <div className="flex items-center justify-between px-3 py-1.5 rounded-t-xl bg-[color:color-mix(in_oklab,var(--card-bg)_60%,transparent)] backdrop-blur-sm w-[98%] mx-auto">
          <div className="flex items-center gap-2">
            <span className="text-[11px] theme-text-muted">
              {false ? (
                'Carregando créditos...'
              ) : (
                `${credits.toLocaleString('pt-BR')} créditos disponíveis`
              )}
            </span>
          </div>
          {userPlanType !== PlanType.PRO && (
            <button
              type="button"
              onClick={() => setIsStripeCheckoutOpen(true)}
              className="text-[11px] font-medium text-[color:var(--nt-primary)] hover:text-[color:var(--nt-primary)]/80 transition-colors px-2 py-0.5 rounded-md hover:bg-[color:var(--nt-primary)]/10"
            >
              Assinar o Paladino Pro
            </button>
          )}
        </div>
      )}

      <div
        className={`relative flex flex-col gap-2 p-2 rounded-2xl border transition-all theme-input ${isDisabled
          ? 'opacity-95'
          : 'focus-within:border-[color:var(--ui-border-strong)] focus-within:ring-2 focus-within:ring-[color:var(--ui-border-strong)]/15'
          }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          multiple
          onChange={(e) => handleFilesSelected(e.target.files)}
        />

        <div className="relative flex-1 min-h-[44px]">
          {isEditableEmpty && (
            <div
              className="absolute inset-0 px-2 py-2 text-sm theme-text-muted pointer-events-none"
              style={{ lineHeight: 1.5 }}
              aria-hidden
            >
              Digite sua mensagem, @ para integrações ou / para comandos...
            </div>
          )}
          <div
            ref={editableRef}
            role="textbox"
            contentEditable={!isDisabled}
            suppressContentEditableWarning
            onInput={handleEditableInput}
            onKeyDown={handleKeyDown}
            onPaste={(e) => {
              e.preventDefault();
              const text = e.clipboardData.getData('text/plain');
              document.execCommand('insertText', false, text);
              handleEditableInput();
            }}
            className="min-h-[44px] max-h-[200px] overflow-auto px-2 py-2 text-sm focus:outline-none theme-text w-full relative z-[1]"
            style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}
          />

          {/* Mention Menu (@ClickUp, etc.) */}
          {showMentionMenu && filteredMentions.length > 0 && (
            <div
              ref={mentionMenuRef}
              className="absolute bottom-full left-0 mb-2 w-full rounded-2xl border theme-border shadow-lg overflow-hidden z-50 theme-bg"
            >
              <div className="px-3 py-2 border-b theme-border">
                <div className="text-xs font-semibold theme-text">Integrações</div>
                <div className="text-[11px] theme-text-muted">
                  Digite para filtrar · ↑↓ navegar · Enter selecionar
                </div>
              </div>
              <div className="max-h-[240px] overflow-auto p-1">
                {filteredMentions.map((m, index) => {
                  const isSelected = index === selectedMentionIndex;
                  return (
                    <button
                      key={m.id}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => insertMention(m)}
                      onMouseEnter={() => setSelectedMentionIndex(index)}
                      className={[
                        'w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left transition-colors',
                        isSelected
                          ? 'bg-[color:color-mix(in_oklab,var(--card-bg)_78%,var(--nt-muted)_22%)]'
                          : 'hover:bg-[color:color-mix(in_oklab,var(--card-bg)_85%,transparent)]',
                      ].join(' ')}
                    >
                      <span className="w-9 h-9 rounded-lg flex items-center justify-center bg-[color:color-mix(in_oklab,var(--card-bg)_90%,transparent)] overflow-hidden">
                        <img src={m.icon} alt="" className="w-5 h-5 object-contain" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-medium theme-text">@{m.name}</span>
                        <span className="block text-[11px] theme-text-muted">Adicionar ao contexto</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Command Menu: só ClickUp (tarefas e listas) — fica aberto até o usuário fechar */}
          {showCommandMenu && (() => {
            const commandMenuTitle = 'ClickUp';
            const commandMenuHint = 'Digite para filtrar · ↑↓ navegar · Enter selecionar';
            return (
            <div
              ref={commandMenuRef}
              className="absolute bottom-full left-0 mb-2 w-full rounded-2xl border theme-border shadow-lg overflow-hidden z-50 bg-white dark:bg-[#18181b]"
            >
              <div className="px-3 py-2 border-b theme-border">
                <div className="text-xs font-semibold theme-text">{commandMenuTitle}</div>
                <div className="text-[11px] theme-text-muted">
                  {commandMenuHint}
                </div>
              </div>

              <div className="max-h-[280px] overflow-auto p-1">
                {clickupLoading ? (
                  <div className="px-3 py-4 text-[11px] theme-text-muted text-center">
                    Carregando tarefas e listas do ClickUp...
                  </div>
                ) : commandMenuList.length > 0 ? (
                  commandMenuList.map((item, index) => {
                    const isSelected = index === selectedCommandIndex;
                    return (
                      <button
                        key={`clickup-${item.id}`}
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        onClick={() => insertCommand(`[ClickUp: ${item.name}](clickup:${item.id})`)}
                        onMouseEnter={() => setSelectedCommandIndex(index)}
                        className={[
                          'w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left transition-colors',
                          isSelected
                            ? 'bg-[color:color-mix(in_oklab,var(--card-bg)_78%,var(--nt-muted)_22%)]'
                            : 'hover:bg-[color:color-mix(in_oklab,var(--card-bg)_85%,transparent)]',
                        ].join(' ')}
                      >
                        <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-[color:color-mix(in_oklab,var(--card-bg)_90%,transparent)] shrink-0 overflow-hidden">
                          <img src="/icons/integrations/clickup.svg" alt="" className="w-4 h-4 object-contain" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-medium theme-text truncate">{item.name}</span>
                          <span className="block text-[11px] theme-text-muted truncate">{item.breadcrumb}</span>
                        </span>
                        <span className="text-[10px] theme-text-muted uppercase shrink-0">{item.type === 'task' ? 'Tarefa' : 'Lista'}</span>
                      </button>
                    );
                  })
                ) : (
                  <div className="px-3 py-4 text-[11px] theme-text-muted text-center">
                    Nenhuma tarefa ou lista encontrada. Digite para buscar.
                  </div>
                )}
              </div>
            </div>
            );
          })()}
        </div>

        <div className="flex items-center justify-between flex-row gap-2">
          <div className="flex flex-row gap-2 items-center">
            <motion.button
              type="button"
              onClick={handlePickFiles}
              disabled={isDisabled || isBusy}
              className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:text-slate-800 hover:theme-card hover:border-card-hover hover:border"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title="Anexar arquivos"
            >
              <svg className="inline-flex size-4 transition-colors fill-icon-soft-400 group-hover:fill-blue-500 dark:fill-foreground" width="20" height="20" viewBox="0 0 20 20">
                <path d="M15.037 2.46a4.85 4.85 0 0 1 0 6.855l-.032.033a4.85 4.85 0 0 1-.801 5.801l-2.388 2.388a4.85 4.85 0 0 1-6.855 0 4.85 4.85 0 0 1 0-6.855l.032-.033a4.85 4.85 0 0 1 .801-5.801L8.181 2.46a4.85 4.85 0 0 1 6.855 0zm-5.971.884L6.677 5.732a3.6 3.6 0 0 0 0 5.087 3.6 3.6 0 0 0 5.087 0l.172-.172c.244-.244.64-.244.884 0s.244.64 0 .884l-.172.172a4.85 4.85 0 0 1-6.855 0l-.041-.042-.053.058a3.6 3.6 0 0 0 5.233 4.934l2.389-2.389a3.6 3.6 0 0 0 0-5.087 3.6 3.6 0 0 0-5.087 0l-.172.172c-.244.244-.64.244-.884 0s-.244-.64 0-.884l.172-.172a4.85 4.85 0 0 1 6.855 0l.039.042.055-.058a3.6 3.6 0 0 0-.145-4.934 3.6 3.6 0 0 0-5.087 0z"></path>
              </svg>
            </motion.button>


            {/* <motion.button
              type="button"
              onClick={handlePickFiles}
              disabled={isDisabled || isBusy}
              className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:text-slate-800 hover:theme-card hover:border-card-hover hover:border"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title="Anexar arquivos"
            >
              <svg className="inline-flex size-4 transition-colors fill-icon-soft-400 group-hover:fill-blue-500 dark:fill-foreground" width="20" height="20" viewBox="0 0 20 20">
                <path d="M14.664 1.5c.978-.004 1.47 1.178.779 1.869l-2.25 2.25a.84.84 0 1 0 1.187 1.189l2.25-2.249c.691-.691 1.873-.199 1.869.776l-.001 1.264c-.008 3.289-.1 3.757-.767 4.574-.102.124-.217.249-.361.393-.991.99-2.059 1.132-5.824 1.13H11.5l-4.934 4.936a2.97 2.97 0 0 1-4.197 0 2.97 2.97 0 0 1 0-4.197l4.936-4.936V8.16c.006-3.529.166-4.567 1.13-5.532l.393-.361c.817-.667 1.285-.759 4.575-.767l1.261-.001zM7.52 10.041l-4.272 4.273c-.673.673-.673 1.764 0 2.438s1.764.673 2.438 0l4.272-4.271c-1.14-.394-2.044-1.298-2.438-2.439zm-2.903 4.52a.83.83 0 0 1 .829.829.83.83 0 0 1-.829.829.83.83 0 0 1-.837-.829.83.83 0 0 1 .829-.829zm9.691-11.817h-.861c-2.939.007-3.327.075-3.831.487-.088.072-.184.16-.3.277-.641.64-.768 1.599-.766 4.943v.304c.001 1.488 1.208 2.696 2.694 2.696h.585c3.119-.006 4.041-.143 4.663-.766l.277-.3c.412-.504.48-.892.487-3.83v-.863L15.26 7.687c-.779.778-2.02.812-2.839.101l-.109-.102c-.814-.814-.813-2.134.001-2.948l1.995-1.996z"></path>
              </svg>
            </motion.button> */}



            {/* LLM Model selector */}
            <div className="relative" ref={llmMenuRef}>
              <button
                type="button"
                onClick={() => setIsLlmMenuOpen((v) => !v)}
                disabled={isDisabled || isBusy}
                aria-expanded={isLlmMenuOpen}
                aria-haspopup="listbox"
                aria-label="Selecionar modelo"
                className={[
                  iconOnly ? 'h-8 w-8' : 'h-8 min-w-0 max-w-[200px]',
                  'inline-flex items-center gap-2 pl-2 pr-2 rounded-lg border',
                  'theme-border theme-card theme-text',
                  'transition-colors',
                  'hover:border-[color:var(--ui-border-strong)] hover:bg-[color:color-mix(in_oklab,var(--card-bg)_92%,transparent)]',
                  'focus:outline-none focus:ring-2 focus:ring-[color:var(--nt-primary)]/25 focus:border-[color:var(--nt-primary)]/50',
                  'disabled:opacity-60 disabled:cursor-not-allowed',
                  iconOnly ? 'justify-center' : '',
                ].join(' ')}
                title="Selecionar modelo de linguagem"
              >
                <span className="flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center overflow-hidden bg-[color:color-mix(in_oklab,var(--card-bg)_85%,var(--nt-muted)_15%)]">
                  {getLlmIcon(selectedLlm.kind === 'model' ? selectedLlm.provider : 'auto')}
                </span>
                {!iconOnly && (
                  <>
                    <span className="min-w-0 flex-1 text-left text-xs font-medium truncate theme-text tabular-nums">
                      {getSelectedLlmDisplay()}
                    </span>
                    <svg
                      className={[
                        'flex-shrink-0 w-3.5 h-3.5 theme-text-muted transition-transform duration-150',
                        isLlmMenuOpen ? 'rotate-180' : '',
                      ].join(' ')}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      aria-hidden
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </>
                )}
              </button>

              {isLlmMenuOpen && (
                <div
                  role="listbox"
                  aria-label="Lista de modelos"
                  className="absolute left-0 bottom-full z-50 mb-2 w-[280px] max-w-[85vw] theme-bg rounded-2xl border theme-border shadow-2xl overflow-hidden ring-1 ring-black/5 dark:ring-white/5 p-1"
                >
                  <div ref={llmListRef} className="max-h-[320px] overflow-y-auto overscroll-contain px-0.5 scrollbar-thin scrollbar-thumb-rounded scrollbar-track-transparent scroll-smooth">
                    {showModels && (
                      <div className="px-2 py-2 text-[11px] font-medium text-[color:var(--foreground-muted)] uppercase tracking-wider">
                        Auto
                      </div>
                    )}

                    {(() => {
                      const active = selectedLlm.kind === 'auto';
                      const highlighted = llmHighlightIndex === 0;
                      return (
                        <motion.button
                          whileTap={{ scale: 0.98 }}
                          type="button"
                          role="option"
                          aria-selected={active}
                          data-llm-index={0}
                          onMouseEnter={() => setLlmHighlightIndex(0)}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleAutoSelection();
                          }}
                          className={[
                            'group w-full flex flex-col px-2 py-2 rounded-xl text-left transition-colors',
                            active ? 'text-[color:var(--nt-primary)]' : 'theme-text',
                            highlighted && !active ? 'bg-[color:color-mix(in_oklab,var(--card-bg)_82%,transparent)]' : '',
                            'hover:bg-[color:color-mix(in_oklab,var(--card-bg)_86%,transparent)]',
                          ].join(' ')}
                        >
                          <div className="flex items-center justify-between w-full">
                            <span className="min-w-0 flex items-center gap-3">
                              <span className="w-4 h-4 flex items-center justify-center">{getLlmIcon('auto')}</span>
                              <span className="text-[13px] font-medium truncate">Auto</span>
                            </span>
                            <motion.div
                              animate={{ backgroundColor: active ? 'var(--nt-primary)' : 'rgb(148 163 184 / 0.35)' }}
                              transition={{ duration: 0.18, ease: 'easeOut' }}
                              className="w-8 h-4 rounded-full flex items-center p-0.5 shadow-inner"
                            >
                              <motion.div
                                animate={{ x: active ? 12 : 0 }}
                                transition={{ type: 'spring', stiffness: 420, damping: 28 }}
                                className="w-3 h-3 rounded-full bg-white shadow-sm"
                              />
                            </motion.div>
                          </div>
                          <AnimatePresence>
                            {(highlighted || active) && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden pl-7 pr-2"
                              >
                                <p className="text-[11px] text-[color:var(--foreground-muted)] leading-tight py-1">
                                  Permite que o sistema escolha dinamicamente o modelo de IA mais eficiente para a sua requisição.
                                </p>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.button>
                      );
                    })()}

                    <AnimatePresence initial={false}>
                      {showModels && (
                        <motion.div
                          key="models-block"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="px-2 py-2 text-[11px] font-medium text-[color:var(--foreground-muted)] uppercase tracking-wider">
                            Modelos
                          </div>

                          <div className="">
                            <input
                              type="search"
                              value={llmSearch}
                              onChange={(e) => {
                                setLlmSearch(e.target.value);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter') {
                                  e.preventDefault();
                                  if (e.key === 'ArrowDown') setLlmHighlightIndex((i) => (i < llmSelectableCount - 1 ? i + 1 : 0));
                                  if (e.key === 'ArrowUp') setLlmHighlightIndex((i) => (i > 0 ? i - 1 : llmSelectableCount - 1));
                                  if (e.key === 'Enter' && llmSelectableOptions[llmHighlightIndex]) {
                                    const o = llmSelectableOptions[llmHighlightIndex];
                                    if (o.kind === 'auto') {
                                      toggleAutoSelection();
                                      return;
                                    }
                                    if (o.kind === 'model') {
                                      setLastManualLlm({ provider: o.providerId, model: o.model.id });
                                      setSelectedLlm({ kind: 'model', provider: o.providerId, model: o.model.id });
                                      setIsLlmMenuOpen(false);
                                    }
                                  }
                                }
                              }}
                              placeholder="Buscar modelo..."
                              className="w-full px-2 py-2 text-sm bg-transparent theme-text placeholder:theme-text-muted focus:outline-none"
                              autoFocus
                            />
                          </div>

                          {llmModelsFilteredByProvider.length === 0 ? (
                            <div className="py-4 text-center text-sm theme-text-muted">
                              Nenhum modelo encontrado
                            </div>
                          ) : (
                            llmModelsMenuEntries.map((entry, idx) => {
                              if (entry.kind === 'provider-header') {
                                return (
                                  <div
                                    key={`provider-${entry.providerId}-${idx}`}
                                    className="mt-2 px-2 py-1.5 text-[12px] font-medium text-[color:var(--foreground-muted)] flex items-center gap-2"
                                  >
                                    <span>{entry.providerName}</span>
                                  </div>
                                );
                              }

                              const active =
                                selectedLlm.kind === 'model' &&
                                selectedLlm.provider === entry.providerId &&
                                selectedLlm.model === entry.model.id;
                              const highlighted = entry.selectableIndex === llmHighlightIndex;
                              const isPaladas = entry.providerId === 'paladas';

                              return (
                                <motion.button
                                  whileTap={{ scale: 0.98 }}
                                  key={`${entry.providerId}-${entry.model.id}`}
                                  type="button"
                                  role="option"
                                  aria-selected={active}
                                  data-llm-index={entry.selectableIndex}
                                  onMouseEnter={() => setLlmHighlightIndex(entry.selectableIndex)}
                                  onClick={() => {
                                    setLastManualLlm({ provider: entry.providerId, model: entry.model.id });
                                    setSelectedLlm({ kind: 'model', provider: entry.providerId, model: entry.model.id });
                                    setIsLlmMenuOpen(false);
                                  }}
                                  className={[
                                    'group w-full flex flex-col px-2 py-2 rounded-xl text-left transition-colors',
                                    active ? 'bg-[color:color-mix(in_oklab,var(--nt-primary)_10%,transparent)] text-[color:var(--nt-primary)]' : 'theme-text',
                                    highlighted && !active ? 'bg-[color:color-mix(in_oklab,var(--card-bg)_82%,transparent)]' : '',
                                    'hover:bg-[color:color-mix(in_oklab,var(--card-bg)_86%,transparent)]',
                                  ].join(' ')}
                                >
                                  <div className="flex items-center justify-between w-full">
                                    <span className="min-w-0 flex items-center gap-3">
                                      <span className="w-4 h-4 flex items-center justify-center overflow-hidden rounded-sm">
                                        {getLlmIcon(entry.providerId)}
                                      </span>
                                      <span className="min-w-0 flex items-center gap-2">
                                        <span className="text-[13px] font-medium truncate">{entry.model.name}</span>
                                        {isPaladas && (
                                          <span className="flex-shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[color:var(--nt-primary)]/15 text-[color:var(--nt-primary)] uppercase tracking-wide">
                                            Padrão
                                          </span>
                                        )}
                                      </span>
                                    </span>
                                    {active && (
                                      <motion.div initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', stiffness: 300, damping: 20 }}>
                                        <Check className="w-4 h-4 text-[color:var(--nt-primary)]" />
                                      </motion.div>
                                    )}
                                  </div>

                                  <AnimatePresence>
                                    {(highlighted || active) && (
                                      <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        className="overflow-hidden pl-7 pr-2"
                                      >
                                        <p className="text-[11px] text-[color:var(--foreground-muted)] leading-tight py-1">
                                          {entry.model.description}
                                        </p>
                                      </motion.div>
                                    )}
                                  </AnimatePresence>
                                </motion.button>
                              );
                            })
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              )}
            </div>
          </div>

          <motion.button
            onClick={handleSubmit}
            disabled={!input.trim() || isBusy || isDisabled}
            className="flex-shrink-0 w-10 h-10 flex items-center justify-center text-white disabled:opacity-50 disabled:cursor-not-allowed"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {isStreaming ? (
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M17.9974 2.11881C17.6079 1.69932 16.9972 1.5422 16.4137 1.48595C15.8006 1.42686 15.0663 1.46616 14.2758 1.56985C12.6903 1.77781 10.7748 2.25919 8.93484 2.85147C7.09369 3.44413 5.29896 4.15721 3.9549 4.84039C3.28597 5.1804 2.70579 5.52444 2.28402 5.85489C2.074 6.01943 1.88236 6.19667 1.7382 6.38531C1.6007 6.56525 1.45604 6.82125 1.45803 7.12985C1.46325 7.93992 2.01492 8.51242 2.6024 8.89667C3.20204 9.28892 3.98299 9.5845 4.75935 9.816C5.54404 10.05 6.36923 10.2313 7.07862 10.3783C7.12511 10.3879 7.21804 10.4072 7.3319 10.4307C7.7607 10.5193 7.9751 10.5636 8.17665 10.5033C8.37818 10.4429 8.53318 10.288 8.84301 9.97809L11.9104 6.91074C12.2358 6.58531 12.7635 6.58531 13.0889 6.91074C13.4143 7.23619 13.4143 7.76383 13.0889 8.08926L10.2284 10.9498C9.91251 11.2657 9.75459 11.4236 9.69485 11.6286C9.63501 11.8336 9.68326 12.0512 9.77968 12.4864C10.1533 14.1737 10.4778 15.5679 10.8016 16.5059C10.9907 17.0538 11.2034 17.528 11.4728 17.8778C11.754 18.2428 12.1365 18.5185 12.6399 18.5408C12.9533 18.5546 13.2139 18.4115 13.3926 18.2787C13.5809 18.1388 13.758 17.9512 13.9223 17.7461C14.2523 17.3338 14.599 16.7633 14.944 16.1039C15.6373 14.7789 16.3715 13.0023 16.9928 11.1747C17.6137 9.34775 18.1311 7.44217 18.3803 5.85842C18.5047 5.06859 18.5662 4.33506 18.532 3.72064C18.4996 3.13797 18.3754 2.52589 17.9974 2.11881Z" fill="url(#paint0_linear_1_13296)" />
                <defs>
                  <linearGradient id="paint0_linear_1_13296" x1="18.2566" y1="2.15562" x2="-1.40093" y2="11.8084" gradientUnits="userSpaceOnUse">
                    <stop stopColor="var(--icon-blue)" />
                    <stop offset="1" stopColor="var(--icon-lavender)" />
                  </linearGradient>
                </defs>
              </svg>
            )}
          </motion.button>
        </div>
      </div>

      {attachments.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {attachments.map((a) => (
            <span
              key={a.id}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs border theme-card theme-border shadow-sm text-[color:var(--foreground)]"
              title={`${a.file.name} (${Math.round((a.file.size / 1024) * 10) / 10} KB)`}
            >
              <span className="max-w-[180px] truncate">{a.file.name}</span>
              <button
                type="button"
                onClick={() => removeAttachment(a.id)}
                className="p-0.5 rounded hover:theme-card-hover text-[color:var(--foreground-muted)] hover:text-[color:var(--foreground)]"
                aria-label={`Remover anexo ${a.file.name}`}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}

      {attachmentError && (
        <p className="text-xs mt-2 theme-text-muted">
          {attachmentError}
        </p>
      )}

      {/* Stripe Checkout Modal */}
      <StripeCheckoutModal
        isOpen={isStripeCheckoutOpen}
        onClose={() => setIsStripeCheckoutOpen(false)}
        amount={8990} // R$ 89,90 in cents
        planName="Paladino Pro - R$ 89,90/mês"
        planId={proPlanId}
        onSuccess={async () => {
          toast.success('Plano Paladino Pro ativado com sucesso!');
          setIsStripeCheckoutOpen(false);
          await Promise.all([refreshCreditsFromAuth(), refreshPlans()]);
        }}
      />
    </div>
  );
}
